/**
 * The Day grid (DNKADM-0300 — the single responsive Day view that replaces the
 * Timeline/Columns/List layout toggle).
 *
 * **Geometry (the unit's written-down decision, amended):** the axis is the
 * FULL operating window in both orientations ({@link dayEnvelope} with
 * `trim: false` — Operator ruling 2026-08-14; DNKADM-0300 originally trimmed
 * the landscape axis to the in-window booking span ±1h, but that made the
 * same horizontal position mean a different time each day). Bookings outside
 * the window collapse into per-lane count chips in fixed side bands, so one
 * 5 AM booking still cannot stretch every row; tapping a band WIDENS the axis
 * to the old union (session state, reset on a date change). The hour track
 * has no per-hour px floor — it flexes to the container, so a full day fits
 * with **no horizontal scroll** at desktop, 1024×768 and 768×1024. Lane rows
 * are `flex: 1` and fill the viewport-sized container; only genuine sub-lane
 * overflow scrolls vertically.
 *
 * **Orientation is responsive, not user-selected** (DNKADM-0300 B7): in
 * LANDSCAPE orientation the axis is horizontal (lanes as rows); in PORTRAIT
 * the portrait treatment renders — lanes as columns, time down the page at a
 * fixed comfortable hour height (vertical scroll is free on a tablet), the
 * full operating window visible, with the same early/late collapse as band
 * rows. DNKADM-0304 U5 keyed the switch on `(orientation: portrait)` rather
 * than the `md` width breakpoint, which large iPads (≥ 900 CSS px wide in
 * portrait) never crossed — so they horizontal-axised in portrait with
 * truncated names. Both variants are in the DOM behind a pure-CSS switch
 * (the DNKADM-0298 idiom — no JS media query, deterministic in jsdom).
 *
 * **The primary interaction** (the spec's lead preservation item): every
 * block — any duration, cancelled included — is a real `<button>` that opens
 * the detail surface on click/tap. Nothing here depends on hover.
 *
 * **Block content** (DNKADM-0300 C14): the time line is REMOVED (the axis
 * states it) and the old ≥1.5h meta gate is gone — every block shows the
 * customer and the service · coach line. Rows with a single sub-lane also
 * show a money-state line (card on file / credits / a pending-charge marker):
 * money state is the front desk's next question, status is already carried by
 * the block treatment (dashed pending / dimmed struck cancelled), and
 * member-vs-guest is not on the reservation object.
 *
 * **The load signal** (DNKADM-0300 D): each lane's gutter carries the single
 * `loadPercent` reading — booked hours over the REAL operating window,
 * measured now→close on today — with its qualifier, and `—` (never 0%) when
 * there is no window or the day is past close.
 */
import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icon } from '@/theme';
import { buildCalendarMaps, type CalendarMaps } from '@/theme/maps/calendar';
import type { Reservation } from '@/api/reservations';
import {
  bayAxis,
  bookedHours,
  dayEnvelope,
  derivePaymentState,
  deriveServiceColor,
  deriveStatusBucket,
  loadPercent,
  loadQualifier,
  localDateValueOf,
  nowHourFraction,
  packLanes,
  paymentBadgeText,
  type HourRange,
  type PaymentState,
  type ResourceAxis,
  type ServiceColor,
  type StatusBucket,
} from './derive';
import { cancelledBlockText, dimDecorativeColor } from './contrastPolicy';
import { blockedRangesOf, type MinuteRange } from '../blockOff';
import { blockedAriaLabel as sharedBlockedAriaLabel, blockedBandSx } from './blockedBand';
import { paymentBadgeMinBlockHeight } from './paymentBadgeFit';
import { serviceWithCoach } from '../coachLabel';
import type { ShiftLike } from '@/utils/shiftValidation';
import { settledFeeLabel } from '@/utils/pendingChargeLabel';
import { todayIn } from '@/components/list/listDate';

type Mode = 'light' | 'dark';

// --- Geometry ------------------------------------------------------------------

/** Landscape lane-gutter width (design: 84px + the load bar's breathing room). */
const GUTTER_WIDTH = 108;
/** The fixed early/late collapse band width (design: 46px). */
const BAND_WIDTH = 46;
/** Landscape hour-header height (design: 34px). */
const HEADER_HEIGHT = 34;
/** Minimum landscape row height per packed sub-lane (touch-safe). */
const SUB_LANE_MIN_HEIGHT = 56;
/** Block inset inside a sub-lane (`top` +3px, `height` -6px). */
const BLOCK_LANE_INSET = 6;
/**
 * A sub-lane tall enough to seat the payment badge under the fullest block (name + service/coach
 * rows; a single-lane block adds the money row) — DNKADM-0456 V-73: at the old 56px floor the
 * landscape badge was clipped by `overflow: hidden`. Derived from the portrait fit arithmetic so
 * the two layouts share one set of row constants.
 */
function laneMinHeight(subLanes: number): number {
  const seat = paymentBadgeMinBlockHeight(true, subLanes === 1) + BLOCK_LANE_INSET;
  return subLanes * Math.max(SUB_LANE_MIN_HEIGHT, seat);
}
/** Portrait per-hour row height (design: 92px — vertical scroll is free). */
const PORTRAIT_HOUR_HEIGHT = 92;
/** Portrait time-gutter width (design: 52px). */
const PORTRAIT_TIME_WIDTH = 52;
/** Portrait lane-header height (design: 44px). */
const PORTRAIT_HEADER_HEIGHT = 44;
/** Portrait early/late band-row height (design: 42px). */
const PORTRAIT_BAND_HEIGHT = 42;

export interface DayGridProps {
  /** The loaded + client-filtered reservations (the shell's grid set). */
  reservations: Reservation[];
  /** The visible `YYYY-MM-DD` date. */
  date: string;
  /** Open the detail surface for a booking — the primary interaction. */
  onSelect: (reservation: Reservation) => void;
  /** The day's operating window, or null when unknown/closed. */
  operating: HourRange | null;
  /** The active resource axis (bays or staff). */
  axis?: ResourceAxis;
  /** The gutter header word ("BAY"/"STAFF"). */
  axisLabel?: string;
  /** The venue IANA zone (fail-soft null). */
  timeZone?: string | null;
  /**
   * The date's block-offs per lane (DNKADM-0373 U2) — each a date exception's open
   * shape for a lane on this date; the grid renders what it CLOSES as a band against its
   * own axis, so the geometry is decided once, here.
   */
  blocks?: LaneBlock[];
  /**
   * A tap on an EMPTY part of a lane's track (DNKADM-0389 U2): the lane key and the start
   * hour (fractional, snapped down to the half hour). Bookings and blocked bands swallow
   * their own clicks, so this fires only for open space. Absent → the track is inert.
   */
  onSlotClick?: (laneKey: string, startHour: number) => void;
  /** A tap on a Blocked band (DNKADM-0389 U3). Absent → bands stay non-interactive images. */
  onBlockSelect?: (block: LaneBlock) => void;
}

/** A date exception (its open shape) for one lane on the visible date. */
export interface LaneBlock {
  laneKey: string;
  isAvailable: boolean;
  shifts: ShiftLike[];
  /** The exception row id (DNKADM-0389 U3 — handed back on a band click), when known. */
  id?: string | null;
}

/** A blocked span on the axis, in fractional hours, and the block it came from. */
interface BlockedSpan {
  startHour: number;
  endHour: number;
  block: LaneBlock;
}

/** The half-hour step a tap on open space snaps DOWN to (DNKADM-0389 U2). */
const SLOT_CLICK_STEP_HOURS = 0.5;

/**
 * The start hour a tap inside a lane's track means: the click's fraction along the axis
 * (x in landscape, y in portrait), snapped down to the half hour and clamped inside the
 * envelope. A zero-size track (no layout) reads as the envelope's start.
 */
function tappedHour(fraction: number, range: HourRange): number {
  const span = range.endHour - range.startHour;
  const raw = range.startHour + Math.max(0, Math.min(1, fraction)) * span;
  const snapped = Math.floor(raw / SLOT_CLICK_STEP_HOURS) * SLOT_CLICK_STEP_HOURS;
  return Math.min(range.endHour - SLOT_CLICK_STEP_HOURS, Math.max(range.startHour, snapped));
}

/** True when a click landed on a booking or a band (they own their clicks), not open space. */
function onInteractiveChild(e: MouseEvent<HTMLElement>): boolean {
  const target = e.target as HTMLElement | null;
  return target !== null && target !== e.currentTarget && target.closest('button') !== null;
}

/**
 * The blocked spans per lane against the visible axis range: a closed exception blocks
 * the whole range; an available one blocks the gaps its shifts leave inside it.
 */
function blockedByLane(blocks: LaneBlock[], range: HourRange): Map<string, BlockedSpan[]> {
  const window: MinuteRange = { start: range.startHour * 60, end: range.endHour * 60 };
  const out = new Map<string, BlockedSpan[]>();
  for (const block of blocks) {
    const spans = blockedRangesOf(block, window).map((r) => ({
      startHour: r.start / 60,
      endHour: r.end / 60,
      block,
    }));
    if (spans.length === 0) continue;
    out.set(block.laneKey, [...(out.get(block.laneKey) ?? []), ...spans]);
  }
  return out;
}

function blockedAriaLabel(laneLabel: string, span: BlockedSpan): string {
  return sharedBlockedAriaLabel(laneLabel, span.startHour, span.endHour);
}

/** A booking placed on the axis by its venue-hour span. */
interface Placed {
  reservation: Reservation;
  startHour: number;
  endHour: number;
  color: ServiceColor;
  bucket: StatusBucket;
  payment: PaymentState | null;
  lane: number;
}

interface PackedLane {
  placed: Placed[];
  lanes: number;
}

/** A whole-hour axis label, e.g. `9 AM`. */
function formatAxisHour(hour: number): string {
  const period = hour >= 12 && hour < 24 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12} ${period}`;
}

/** `Xh`/`X.5h` for the gutter's booked-hours sublabel. */
function formatHours(hours: number): string {
  const rounded = Math.round(hours * 2) / 2;
  return `${rounded % 1 === 0 ? rounded : rounded.toFixed(1)}h`;
}

/** The member label for a block: contact name → service → id → em-dash. */
function customerLabel(reservation: Reservation): string {
  return reservation.contactName ?? reservation.serviceName ?? reservation.bookingId ?? '—';
}

/**
 * The block's `service with coach` line (DNKADM-0448), or null when neither is known. The coach
 * comes from the v2 canonical `assignedStaffName`, NOT the `staff` alias — the
 * alias deliberately mirrors the RESOURCE on staffless bookings (the bay axis'
 * grouping contract, `api/reservations.ts`), which put the bay's name in the
 * coach slot on every staffless block (DNKADM-0304 U2, the DNKADM-0303 U7
 * class). No staff → the segment drops entirely.
 */
function serviceCoachLine(reservation: Reservation): string | null {
  return serviceWithCoach(reservation);
}

/**
 * The block's money-state line (DNKADM-0300 C14) — card on file / credits,
 * with a pending-charge marker; null when there is nothing to say.
 */
function moneyLine(reservation: Reservation): string | null {
  const parts: string[] = [];
  if (reservation.paymentMode === 'card_on_file') parts.push('Card on file');
  else if (reservation.creditsCharged !== null && reservation.creditsCharged > 0) {
    parts.push(`${reservation.creditsCharged} cr`);
  }
  // DNKADM-0489 follow-up: dink-api keeps the pending-charge row once it settles, so a
  // charged, failed or cancelled fee says so; pending (or an unknown status) stays
  // "charge pending". The drawer's charge block reads the same mapper.
  const charge = reservation.pendingCharge;
  if (charge) parts.push(settledFeeLabel(charge.status) ?? 'charge pending');
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** The block's accessible name — the state must carry to AT, not only to color. */
function blockAriaLabel(placed: Placed): string {
  const cancelled = placed.bucket === 'cancelled';
  // The payment-state text token (DNKADM-0350 U2) rides in the accessible name
  // too — the visible corner badge is `aria-hidden` (it would otherwise
  // double-announce), so this is the ONLY announcement for a packed block too
  // small to show the badge's text legibly. Omitted for a cancelled booking —
  // the badge itself is (money state is moot once the slot is gone).
  // DNKADM-0402 U5: an unpaid booking announces its AMOUNT here too — the badge is
  // `aria-hidden`, so this is the only place the figure reaches a screen reader.
  const payment =
    cancelled || placed.payment === null
      ? ''
      : `, ${paymentBadgeText(placed.reservation, placed.payment)}`;
  const pending = placed.bucket === 'pending' ? ', pending confirmation' : '';
  return `${customerLabel(placed.reservation)}, ${placed.reservation.serviceName ?? 'reservation'}${payment}${pending}${cancelled ? ', cancelled' : ''}`;
}

/**
 * The block's payment-state badge (DNKADM-0350 U2; moved off the corner in
 * DNKADM-0393 U3) — a small colored chip carrying the short text token,
 * noticeable across a full grid without reading text (color) while never
 * relying on color alone (the token).
 *
 * **It sits at the BOTTOM of the block, in flow — not in the top-right corner.**
 * As an absolutely-positioned corner overlay it printed itself over the member's
 * name on a narrow card (iPad) or a short booking: the name is the one thing a
 * block that small cannot afford to lose, and money state is never worth covering
 * it. Laid out after the text instead of on top of it, the badge can no longer
 * overlap anything; `mt: 'auto'` keeps it on the bottom edge where there is spare
 * height, and it ellipsises rather than pushing the block wider.
 *
 * `aria-hidden`: the block button's own {@link blockAriaLabel} already carries the
 * same text, so this stays a silent visual duplicate — which is also why DROPPING
 * it on a block too small to seat it (see {@link paymentBadgeMinBlockHeight}), or
 * having it clipped where the height cannot be checked, costs the accessible name
 * nothing.
 */
function PaymentBadge({
  reservation,
  payment,
  cm,
}: {
  reservation: Reservation;
  payment: PaymentState | null;
  cm: CalendarMaps;
}) {
  // DNKADM-0437 V-20: no derivable payment state means no badge.
  if (payment === null) return null;
  const pair = cm.paymentState[payment];
  return (
    <Box
      aria-hidden
      sx={{
        mt: 'auto',
        alignSelf: 'flex-start',
        flex: 'none',
        maxWidth: '100%',
        px: '5px',
        py: '1px',
        borderRadius: '999px',
        fontSize: 9,
        fontWeight: 700,
        lineHeight: 1.5,
        letterSpacing: '.2px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        bgcolor: pair.bg,
        color: pair.fg,
      }}
    >
      {paymentBadgeText(reservation, payment)}
    </Box>
  );
}

/** Group + pack the day's placeable bookings per axis lane. */
function packByLane(
  dayBookings: Reservation[],
  axis: ResourceAxis,
  timeZone: string | null,
  mode: Mode,
): Map<string, PackedLane> {
  const laneKeys = new Set(axis.lanes.map((l) => l.key));
  const byLane = new Map<string, Reservation[]>();
  for (const r of dayBookings) {
    const resolved = axis.keyOf(r);
    const key = resolved !== axis.unassignedKey && !laneKeys.has(resolved) ? axis.unassignedKey : resolved;
    const list = byLane.get(key);
    if (list) list.push(r);
    else byLane.set(key, [r]);
  }
  const packed = new Map<string, PackedLane>();
  for (const lane of axis.lanes) {
    const rows = byLane.get(lane.key) ?? [];
    const packing = packLanes(rows, timeZone);
    packed.set(lane.key, {
      lanes: packing.lanes,
      placed: packing.placed.map((p) => ({
        reservation: p.reservation,
        startHour: p.startHour,
        endHour: p.endHour,
        lane: p.lane,
        color: deriveServiceColor(p.reservation.serviceName, mode),
        bucket: deriveStatusBucket(p.reservation.status),
        payment: derivePaymentState(p.reservation),
      })),
    });
  }
  return packed;
}

/** The block's status treatment (shared with the old layouts' vocabulary). */
function blockTreatment(placed: Placed, cm: CalendarMaps) {
  const cancelled = placed.bucket === 'cancelled';
  const pending = placed.bucket === 'pending';
  return {
    cancelled,
    style: {
      background: cancelled ? dimDecorativeColor(cm.cancelledFill) : placed.color.bg,
      border: pending
        ? `1px dashed ${placed.color.main}`
        : `1px solid ${cancelled ? dimDecorativeColor(placed.color.bg) : placed.color.bg}`,
      borderLeft: `4px solid ${cancelled ? dimDecorativeColor(placed.color.main) : placed.color.main}`,
    },
  };
}

// --- The responsive Day grid ----------------------------------------------------

export function DayGrid({
  reservations,
  date,
  onSelect,
  onSlotClick,
  onBlockSelect,
  operating,
  axis: axisProp,
  axisLabel = 'BAY',
  timeZone = null,
  blocks = [],
}: DayGridProps) {
  const mode: Mode = useTheme().palette.mode === 'dark' ? 'dark' : 'light';
  const cm = buildCalendarMaps(mode);
  // Tap-to-widen (the geometry decision): session state, reset on date change.
  const [expanded, setExpanded] = useState(false);
  useEffect(() => setExpanded(false), [date]);

  const axis = axisProp ?? bayAxis(reservations);
  const dayBookings = useMemo(
    () => reservations.filter((r) => localDateValueOf(r.startTime, timeZone) === date),
    [reservations, date, timeZone],
  );
  // Today on the venue calendar (DNKADM-0439) — drives the "now" line + the load qualifier.
  const today = todayIn(timeZone);
  const isToday = date === today;

  // Both orientations render the FULL operating window (`trim: false`,
  // Operator ruling 2026-08-14): the trimmed axis was the day's booked span
  // ±1h, so the same horizontal position meant a different time each day —
  // for a board read all day, a stable axis beats the reclaimed width, and
  // with no per-hour px floor the whole window fits. The early/late partition
  // and tap-to-widen still catch genuinely out-of-window bookings, and
  // `expanded` still restores the full union.
  const env = useMemo(
    () => dayEnvelope(dayBookings, operating, { expanded, trim: false, timeZone }),
    [dayBookings, operating, expanded, timeZone],
  );

  const packed = useMemo(
    () => packByLane(dayBookings, axis, timeZone, mode),
    [dayBookings, axis, timeZone, mode],
  );
  // Block-offs (DNKADM-0373 U2) against the SAME axis range the blocks use.
  const blocked = useMemo(() => blockedByLane(blocks, env.range), [blocks, env.range]);

  // A booking's routed lane key — same off-roster → "Unassigned" routing the
  // packer uses, so the load reading and the blocks agree on lane membership.
  const laneKeySet = useMemo(() => new Set(axis.lanes.map((l) => l.key)), [axis]);
  const routedKey = (r: Reservation): string => {
    const resolved = axis.keyOf(r);
    return resolved !== axis.unassignedKey && !laneKeySet.has(resolved)
      ? axis.unassignedKey
      : resolved;
  };

  // The single load definition per lane (capacity 1) — now→close on today.
  const nowHour = nowHourFraction(new Date(), timeZone);
  const qualifier = loadQualifier(date, today);
  const laneLoad = (laneKey: string): number | null =>
    loadPercent(
      dayBookings.filter((r) => routedKey(r) === laneKey),
      operating,
      { isToday, nowHour, timeZone },
    );

  // Orientation IS the switch (DNKADM-0304 U5). The original `md` width
  // breakpoint was a proxy that fails on large iPads: a 12.9"/13" iPad is
  // ≥ 900 CSS px wide IN PORTRAIT, so it resolved the landscape variant and
  // rendered the horizontal axis with truncated customer names ("Peyt…") —
  // the portrait branch simply never fired. Width cannot discriminate
  // 1024-wide-portrait from 1024-wide-landscape; `(orientation: portrait)`
  // can, and matches the design sheet's words verbatim ("Portrait turns the
  // axis vertical"). Still a pure-CSS switch — both variants stay in the DOM
  // (the DNKADM-0298 idiom, deterministic in jsdom).
  return (
    <>
      {/* Both display rules live in media queries with NO base rule — a real
          browser always matches exactly one orientation, and jsdom (which
          evaluates no media queries) keeps both variants queryable, exactly
          as the old breakpoint-object styles behaved. */}
      <LandscapeDay
        sxDisplay={{
          '@media (orientation: landscape)': { display: 'flex' },
          '@media (orientation: portrait)': { display: 'none' },
        }}
        env={env}
        packed={packed}
        blocked={blocked}
        axis={axis}
        axisLabel={axisLabel}
        laneLoad={laneLoad}
        routedKey={routedKey}
        qualifier={qualifier}
        isToday={isToday}
        timeZone={timeZone}
        expanded={expanded}
        onWiden={() => setExpanded(true)}
        onSelect={onSelect}
        onSlotClick={onSlotClick}
        onBlockSelect={onBlockSelect}
        cm={cm}
        mode={mode}
      />
      <PortraitDay
        sxDisplay={{
          '@media (orientation: landscape)': { display: 'none' },
          '@media (orientation: portrait)': { display: 'flex' },
        }}
        env={env}
        packed={packed}
        blocked={blocked}
        axis={axis}
        laneLoad={laneLoad}
        qualifier={qualifier}
        isToday={isToday}
        timeZone={timeZone}
        expanded={expanded}
        onWiden={() => setExpanded(true)}
        onSelect={onSelect}
        onSlotClick={onSlotClick}
        onBlockSelect={onBlockSelect}
        cm={cm}
        mode={mode}
      />
    </>
  );
}

// --- Landscape orientation: lanes as rows, horizontal axis -----------------------

function LandscapeDay({
  sxDisplay,
  env,
  packed,
  blocked,
  axis,
  axisLabel,
  laneLoad,
  routedKey,
  qualifier,
  isToday,
  timeZone,
  expanded,
  onWiden,
  onSelect,
  onSlotClick,
  onBlockSelect,
  cm,
  mode,
}: {
  sxDisplay: object;
  env: ReturnType<typeof dayEnvelope>;
  packed: Map<string, PackedLane>;
  blocked: Map<string, BlockedSpan[]>;
  axis: ResourceAxis;
  axisLabel: string;
  laneLoad: (laneKey: string) => number | null;
  routedKey: (r: Reservation) => string;
  qualifier: string;
  isToday: boolean;
  timeZone: string | null;
  expanded: boolean;
  onWiden: () => void;
  onSelect: (r: Reservation) => void;
  onSlotClick?: ((laneKey: string, startHour: number) => void) | undefined;
  onBlockSelect?: ((block: LaneBlock) => void) | undefined;
  cm: CalendarMaps;
  mode: Mode;
}) {
  const { startHour, endHour } = env.range;
  const span = endHour - startHour;
  const hasEarly = env.early.length > 0;
  const hasLate = env.late.length > 0;
  const tickStep = span > 14 ? 2 : 1;
  const pct = (h: number) => ((h - startHour) / span) * 100;
  const nowHour = nowHourFraction(new Date(), timeZone);

  const ticks: ReactNode[] = [];
  for (let h = startHour; h < endHour; h += tickStep) {
    ticks.push(
      <Box
        key={h}
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: `${pct(h)}%`,
          borderLeft: `1px solid ${cm.gridLine}`,
          pl: '6px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontSize: 11, color: cm.textMuted, whiteSpace: 'nowrap' }}>
          {formatAxisHour(h)}
        </Typography>
      </Box>,
    );
  }

  return (
    <Box
      data-testid="day-grid-landscape"
      sx={{
        ...sxDisplay,
        flexDirection: 'column',
        // Rows fill the height (DNKADM-0300 C12) — a viewport-sized container
        // instead of the fixed 560px; overflow scrolls vertically only when
        // sub-lane packing genuinely exceeds it. Never horizontally.
        height: 'clamp(420px, calc(100vh - 340px), 780px)',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: cm.surface,
        overflow: 'hidden',
      }}
    >
      {/* Hour header */}
      <Box sx={{ display: 'flex', height: HEADER_HEIGHT, flex: 'none', borderBottom: `1px solid ${cm.gutterBorder}`, bgcolor: cm.tableHeaderBg }}>
        <Box sx={{ width: GUTTER_WIDTH, flex: 'none', borderRight: `1px solid ${cm.gutterBorder}`, display: 'flex', alignItems: 'center', px: '10px', fontSize: 11, fontWeight: 600, color: cm.textMuted, letterSpacing: '.5px' }}>
          {axisLabel}
        </Box>
        {hasEarly && <BandHeader label={`before ${formatAxisHour(startHour)}`} cm={cm} />}
        <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>{ticks}</Box>
        {hasLate && <BandHeader label={`after ${formatAxisHour(endHour)}`} cm={cm} />}
      </Box>

      {/* Lane rows — flex: 1 each, min height by sub-lane count. */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {axis.lanes.map((lane) => {
          const pack = packed.get(lane.key) ?? { placed: [], lanes: 1 };
          const load = laneLoad(lane.key);
          const booked = bookedHours(pack.placed.map((p) => p.reservation));
          const earlyCount = env.early.filter((r) => routedKey(r) === lane.key).length;
          const lateCount = env.late.filter((r) => routedKey(r) === lane.key).length;
          return (
            <Box
              key={lane.key}
              sx={{
                flex: 1,
                minHeight: laneMinHeight(pack.lanes),
                display: 'flex',
                borderBottom: `1px solid ${cm.gridLine}`,
              }}
            >
              {/* Gutter: label, booked sublabel, the load signal. */}
              <Box sx={{ width: GUTTER_WIDTH, flex: 'none', borderRight: `1px solid ${cm.gutterBorder}`, p: '10px', display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: cm.textPrimary, lineHeight: 1.2 }} noWrap>
                  {lane.label}
                </Typography>
                <Typography sx={{ fontSize: 11, color: cm.textMuted }}>
                  {booked > 0 ? `${formatHours(booked)} booked` : 'Available'}
                </Typography>
                <Box
                  role="img"
                  aria-label={`${lane.label} load: ${load === null ? 'not measurable' : `${load}%`}, ${qualifier}`}
                  sx={{ mt: '2px' }}
                >
                  <Box sx={{ height: 5, borderRadius: 3, bgcolor: cm.neutralStatusBg, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${load ?? 0}%`, bgcolor: cm.laneSwatch, borderRadius: 3 }} />
                  </Box>
                  {/* The qualifier states WHICH question the number answered
                      (DNKADM-0300 D) — it must never ellipsis away, so it
                      WRAPS inside the gutter instead (DNKADM-0304 U6). */}
                  <Typography
                    variant="mono"
                    component="div"
                    sx={{ fontSize: 10, color: cm.textMuted, mt: '2px', lineHeight: 1.3 }}
                  >
                    {load === null ? '—' : `${load}% · ${qualifier}`}
                  </Typography>
                </Box>
              </Box>

              {hasEarly && (
                <BandCell count={earlyCount} widenLabel={`${earlyCount} earlier — widen the axis`} onWiden={onWiden} cm={cm} />
              )}

              {/* The track: blocks positioned by % of the envelope. A tap on open space books
                  there (DNKADM-0389 U2); bookings and bands own their own clicks. */}
              <Box
                data-testid="day-lane-track"
                data-lane={lane.key}
                sx={{ flex: 1, position: 'relative', minWidth: 0, cursor: onSlotClick ? 'pointer' : 'default' }}
                onClick={(e) => {
                  if (!onSlotClick || onInteractiveChild(e)) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const fraction = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0;
                  onSlotClick(lane.key, tappedHour(fraction, env.range));
                }}
              >
                {Array.from({ length: Math.ceil(span / tickStep) }, (_, i) => (
                  <Box key={i} sx={{ position: 'absolute', top: 0, bottom: 0, left: `${pct(startHour + i * tickStep)}%`, borderLeft: `1px solid ${cm.gridLine}`, opacity: 0.6 }} />
                ))}
                {isToday && nowHour > startHour && nowHour < endHour && (
                  <Box data-testid="day-now-line" sx={{ position: 'absolute', top: 0, bottom: 0, left: `${pct(nowHour)}%`, width: 2, bgcolor: cm.nowLine, zIndex: 4 }} />
                )}
                {(blocked.get(lane.key) ?? []).map((b) => (
                  <Box
                    key={`blocked-${b.startHour}-${b.endHour}`}
                    {...(onBlockSelect
                      ? {
                          component: 'button' as const,
                          type: 'button' as const,
                          onClick: (e: MouseEvent) => {
                            e.stopPropagation();
                            onBlockSelect(b.block);
                          },
                        }
                      : { role: 'img' })}
                    aria-label={blockedAriaLabel(lane.label, b)}
                    data-testid="day-blocked-band"
                    style={{
                      left: `calc(${pct(b.startHour)}% + 1px)`,
                      width: `calc(${((b.endHour - b.startHour) / span) * 100}% - 2px)`,
                      top: 2,
                      bottom: 2,
                    }}
                    sx={{ ...blockedBandSx(cm), ...(onBlockSelect ? { border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left', pointerEvents: 'auto' } : {}) }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: cm.textMuted, whiteSpace: 'nowrap' }}>
                      Blocked
                    </Typography>
                  </Box>
                ))}
                {pack.placed.map((placed) => {
                  const clampedStart = Math.max(placed.startHour, startHour);
                  const clampedEnd = Math.min(placed.endHour, endHour);
                  if (clampedEnd <= clampedStart) return null;
                  const t = blockTreatment(placed, cm);
                  const single = pack.lanes === 1;
                  return (
                    <Box
                      key={placed.reservation.reservationUuid ?? `${placed.startHour}-${placed.lane}`}
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(placed.reservation);
                      }}
                      aria-label={blockAriaLabel(placed)}
                      style={{
                        left: `calc(${pct(clampedStart)}% + 2px)`,
                        width: `calc(${((clampedEnd - clampedStart) / span) * 100}% - 4px)`,
                        top: `calc(${(placed.lane / pack.lanes) * 100}% + 3px)`,
                        height: `calc(${100 / pack.lanes}% - 6px)`,
                        ...t.style,
                      }}
                      sx={{
                        position: 'absolute',
                        textAlign: 'left',
                        font: 'inherit',
                        cursor: 'pointer',
                        borderRadius: '8px',
                        p: '6px 8px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        boxShadow: t.cancelled ? cm.blockShadowCancelled : cm.blockShadow,
                        zIndex: 3,
                      }}
                    >
                      <Stack direction="row" sx={{ alignItems: 'center', gap: '4px', overflow: 'hidden' }}>
                        {t.cancelled && (
                          <Icon name="cancel" sx={{ fontSize: 12, color: cancelledBlockText(mode), flex: 'none' }} />
                        )}
                        <Typography
                          style={{ textDecoration: t.cancelled ? 'line-through' : 'none' }}
                          sx={{ fontSize: 13, fontWeight: 600, color: t.cancelled ? cancelledBlockText(mode) : cm.textStrong, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, lineHeight: 1.25 }}
                        >
                          {customerLabel(placed.reservation)}
                        </Typography>
                      </Stack>
                      {serviceCoachLine(placed.reservation) && (
                        <Typography sx={{ fontSize: 11, fontWeight: 500, color: t.cancelled ? cancelledBlockText(mode) : placed.color.main, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.25 }}>
                          {serviceCoachLine(placed.reservation)}
                        </Typography>
                      )}
                      {single && moneyLine(placed.reservation) && (
                        <Typography sx={{ fontSize: 10.5, color: t.cancelled ? cancelledBlockText(mode) : cm.textMeta, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {moneyLine(placed.reservation)}
                        </Typography>
                      )}
                      {/* Payment-state badge (DNKADM-0350 U2; bottom of the block,
                          not the corner, since DNKADM-0393 U3) — Day view only
                          (this file never renders Week); not offered for a
                          cancelled booking, whose money state is moot once the
                          slot is gone. LAST child on purpose: in flow after the
                          text, it can never overlap the name — that holds here
                          unconditionally.
                          A landscape block's height comes from the LANE, so the lane
                          row's floor (`laneMinHeight`) is what guarantees the
                          badge a seat (DNKADM-0456 V-73): it is sized from the same
                          row constants as the portrait fit check. */}
                      {!t.cancelled && (
                        <PaymentBadge
                          reservation={placed.reservation}
                          payment={placed.payment}
                          cm={cm}
                        />
                      )}
                    </Box>
                  );
                })}
                {pack.placed.length === 0 && earlyCount === 0 && lateCount === 0 && !blocked.has(lane.key) && (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 13, color: cm.avgLabel }}>Open all day</Typography>
                  </Box>
                )}
              </Box>

              {hasLate && (
                <BandCell count={lateCount} widenLabel={`${lateCount} later — widen the axis`} onWiden={onWiden} cm={cm} />
              )}
            </Box>
          );
        })}
      </Box>

      {expanded && (
        <Box sx={{ flex: 'none', px: 1.5, py: 0.5, borderTop: `1px solid ${cm.gridLine}` }}>
          <Typography sx={{ fontSize: 11, color: cm.textMuted }}>
            Axis widened to show out-of-hours bookings — change the date to trim it again.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/** A fixed collapse-band header cell. */
function BandHeader({ label, cm }: { label: string; cm: CalendarMaps }) {
  return (
    <Box sx={{ width: BAND_WIDTH, flex: 'none', borderRight: `1px solid ${cm.gridLine}`, bgcolor: cm.cancelledFill, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      <Typography variant="mono" sx={{ fontSize: 9, color: cm.textMuted, whiteSpace: 'nowrap' }}>
        {label}
      </Typography>
    </Box>
  );
}

/** A per-lane collapse-band cell: the count chip is the tap-to-widen control. */
function BandCell({
  count,
  widenLabel,
  onWiden,
  cm,
}: {
  count: number;
  widenLabel: string;
  onWiden: () => void;
  cm: CalendarMaps;
}) {
  return (
    <Box sx={{ width: BAND_WIDTH, flex: 'none', borderRight: `1px solid ${cm.gridLine}`, bgcolor: cm.cancelledFill, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {count > 0 && (
        <Box
          component="button"
          type="button"
          aria-label={widenLabel}
          onClick={onWiden}
          sx={{
            font: 'inherit',
            cursor: 'pointer',
            minWidth: 32,
            minHeight: 32,
            borderRadius: '999px',
            border: `1px solid ${cm.gutterBorder}`,
            bgcolor: cm.tableHeaderBg,
            color: cm.textPrimary,
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  );
}

// --- Portrait orientation: lanes as columns, vertical axis -----------------------

function PortraitDay({
  sxDisplay,
  env,
  packed,
  blocked,
  axis,
  laneLoad,
  qualifier,
  isToday,
  timeZone,
  expanded,
  onWiden,
  onSelect,
  onSlotClick,
  onBlockSelect,
  cm,
  mode,
}: {
  sxDisplay: object;
  env: ReturnType<typeof dayEnvelope>;
  packed: Map<string, PackedLane>;
  blocked: Map<string, BlockedSpan[]>;
  axis: ResourceAxis;
  laneLoad: (laneKey: string) => number | null;
  /** The load qualifier — rides every load reading (DNKADM-0304 U6). */
  qualifier: string;
  isToday: boolean;
  timeZone: string | null;
  expanded: boolean;
  onWiden: () => void;
  onSelect: (r: Reservation) => void;
  onSlotClick?: ((laneKey: string, startHour: number) => void) | undefined;
  onBlockSelect?: ((block: LaneBlock) => void) | undefined;
  cm: CalendarMaps;
  mode: Mode;
}) {
  const { startHour, endHour } = env.range;
  const span = endHour - startHour;
  const trackHeight = span * PORTRAIT_HOUR_HEIGHT;
  const pct = (h: number) => ((h - startHour) / span) * 100;
  const nowHour = nowHourFraction(new Date(), timeZone);

  return (
    <Box
      data-testid="day-grid-portrait"
      sx={{
        ...sxDisplay,
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: cm.surface,
        overflow: 'hidden',
      }}
    >
      {/* Lane headers */}
      <Box sx={{ display: 'flex', height: PORTRAIT_HEADER_HEIGHT, flex: 'none', borderBottom: `1px solid ${cm.gutterBorder}`, bgcolor: cm.tableHeaderBg }}>
        <Box sx={{ width: PORTRAIT_TIME_WIDTH, flex: 'none', borderRight: `1px solid ${cm.gutterBorder}` }} />
        {axis.lanes.map((lane) => {
          const load = laneLoad(lane.key);
          return (
            <Box
              key={lane.key}
              sx={{
                flex: 1,
                minWidth: 0,
                borderRight: `1px solid ${cm.gridLine}`,
                px: '8px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '1px',
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: cm.textPrimary, lineHeight: 1.2 }} noWrap>
                {lane.label}
              </Typography>
              {/* The load carries its qualifier here too (DNKADM-0304 U6) —
                  a bare percentage is exactly the ambiguous number DNKADM-0300
                  D forbids. Second line so a narrow column never clips it. */}
              <Typography variant="mono" sx={{ fontSize: 10, color: cm.textMuted, lineHeight: 1.2 }}>
                {load === null ? '—' : `${load}% · ${qualifier}`}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
        {env.early.length > 0 && (
          <PortraitBandRow
            label={`${env.early.length} ${env.early.length === 1 ? 'booking' : 'bookings'} before ${formatAxisHour(startHour)}`}
            onWiden={onWiden}
            cm={cm}
          />
        )}
        <Box sx={{ display: 'flex', height: trackHeight, position: 'relative' }}>
          {/* Time gutter */}
          <Box sx={{ width: PORTRAIT_TIME_WIDTH, flex: 'none', position: 'relative', borderRight: `1px solid ${cm.gutterBorder}` }}>
            {Array.from({ length: span }, (_, i) => (
              <Box key={i} sx={{ position: 'absolute', left: 0, right: '6px', top: `${pct(startHour + i)}%`, textAlign: 'right', pt: '2px' }}>
                <Typography variant="mono" sx={{ fontSize: 10, color: cm.textMuted }}>
                  {formatAxisHour(startHour + i)}
                </Typography>
              </Box>
            ))}
          </Box>
          {axis.lanes.map((lane) => {
            const pack = packed.get(lane.key) ?? { placed: [], lanes: 1 };
            return (
              <Box
                key={lane.key}
                data-testid="day-lane-track"
                data-lane={lane.key}
                sx={{ flex: 1, minWidth: 0, position: 'relative', borderRight: `1px solid ${cm.gridLine}`, cursor: onSlotClick ? 'pointer' : 'default' }}
                onClick={(e) => {
                  if (!onSlotClick || onInteractiveChild(e)) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const fraction = rect.height > 0 ? (e.clientY - rect.top) / rect.height : 0;
                  onSlotClick(lane.key, tappedHour(fraction, env.range));
                }}
              >
                {Array.from({ length: span }, (_, i) => (
                  <Box key={i} sx={{ position: 'absolute', left: 0, right: 0, top: `${pct(startHour + i)}%`, borderTop: `1px solid ${cm.gridLine}`, opacity: 0.6 }} />
                ))}
                {isToday && nowHour > startHour && nowHour < endHour && (
                  <Box sx={{ position: 'absolute', left: 0, right: 0, top: `${pct(nowHour)}%`, height: 2, bgcolor: cm.nowLine, zIndex: 4 }} />
                )}
                {(blocked.get(lane.key) ?? []).map((b) => (
                  <Box
                    key={`blocked-${b.startHour}-${b.endHour}`}
                    {...(onBlockSelect
                      ? {
                          component: 'button' as const,
                          type: 'button' as const,
                          onClick: (e: MouseEvent) => {
                            e.stopPropagation();
                            onBlockSelect(b.block);
                          },
                        }
                      : { role: 'img' })}
                    aria-label={blockedAriaLabel(lane.label, b)}
                    data-testid="day-blocked-band"
                    style={{
                      top: `calc(${pct(b.startHour)}% + 1px)`,
                      height: `calc(${((b.endHour - b.startHour) / span) * 100}% - 2px)`,
                      left: 2,
                      right: 2,
                    }}
                    sx={{ ...blockedBandSx(cm), ...(onBlockSelect ? { border: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left', pointerEvents: 'auto' } : {}) }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: cm.textMuted, whiteSpace: 'nowrap' }}>
                      Blocked
                    </Typography>
                  </Box>
                ))}
                {pack.placed.map((placed) => {
                  const clampedStart = Math.max(placed.startHour, startHour);
                  const clampedEnd = Math.min(placed.endHour, endHour);
                  if (clampedEnd <= clampedStart) return null;
                  const t = blockTreatment(placed, cm);
                  return (
                    <Box
                      key={placed.reservation.reservationUuid ?? `${placed.startHour}-${placed.lane}`}
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelect(placed.reservation);
                      }}
                      aria-label={blockAriaLabel(placed)}
                      style={{
                        top: `calc(${pct(clampedStart)}% + 2px)`,
                        height: `calc(${((clampedEnd - clampedStart) / span) * 100}% - 4px)`,
                        left: `calc(${(placed.lane / pack.lanes) * 100}% + 2px)`,
                        width: `calc(${100 / pack.lanes}% - 4px)`,
                        ...t.style,
                      }}
                      sx={{
                        position: 'absolute',
                        textAlign: 'left',
                        font: 'inherit',
                        cursor: 'pointer',
                        borderRadius: '8px',
                        p: '6px 8px',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        boxShadow: t.cancelled ? cm.blockShadowCancelled : cm.blockShadow,
                        zIndex: 3,
                      }}
                    >
                      <Stack direction="row" sx={{ alignItems: 'center', gap: '4px', overflow: 'hidden' }}>
                        {t.cancelled && (
                          <Icon name="cancel" sx={{ fontSize: 12, color: cancelledBlockText(mode), flex: 'none' }} />
                        )}
                        <Typography
                          style={{ textDecoration: t.cancelled ? 'line-through' : 'none' }}
                          sx={{ fontSize: 13.5, fontWeight: 600, color: t.cancelled ? cancelledBlockText(mode) : cm.textStrong, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, lineHeight: 1.2 }}
                        >
                          {customerLabel(placed.reservation)}
                        </Typography>
                      </Stack>
                      {serviceCoachLine(placed.reservation) && (
                        <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: t.cancelled ? cancelledBlockText(mode) : placed.color.main, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {serviceCoachLine(placed.reservation)}
                        </Typography>
                      )}
                      {moneyLine(placed.reservation) && (
                        <Typography sx={{ fontSize: 10.5, color: t.cancelled ? cancelledBlockText(mode) : cm.textMeta, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {moneyLine(placed.reservation)}
                        </Typography>
                      )}
                      {/* Payment-state badge (DNKADM-0350 U2; bottom of the block,
                          not the corner, since DNKADM-0393 U3) — LAST child on
                          purpose: in flow after the text, it can never overlap the
                          name. This is the iPad layout the badge covered names on,
                          and the one where a block's height IS its duration, so the
                          fit is checkable: a block too short to seat the badge under
                          the rows THIS booking renders drops it rather than letting
                          `overflow: hidden` cut it into a sliver (the `aria-label`
                          still carries the state). Not offered for a cancelled
                          booking, whose money state is moot once the slot is gone. */}
                      {!t.cancelled &&
                        // The block's own height, in px: its duration at the portrait
                        // hour height, less the 4px inset the style above applies.
                        (clampedEnd - clampedStart) * PORTRAIT_HOUR_HEIGHT - 4 >=
                          paymentBadgeMinBlockHeight(
                            serviceCoachLine(placed.reservation) !== null,
                            moneyLine(placed.reservation) !== null,
                          ) && (
                          <PaymentBadge
                            reservation={placed.reservation}
                            payment={placed.payment}
                            cm={cm}
                          />
                        )}
                    </Box>
                  );
                })}
                {pack.placed.length === 0 && (
                  <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: 12.5, color: cm.avgLabel }}>Open all day</Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
        {env.late.length > 0 && (
          <PortraitBandRow
            label={`${env.late.length} ${env.late.length === 1 ? 'booking' : 'bookings'} after ${formatAxisHour(endHour)}`}
            onWiden={onWiden}
            cm={cm}
          />
        )}
        {expanded && (
          <Box sx={{ px: 1.5, py: 0.75, borderTop: `1px solid ${cm.gridLine}` }}>
            <Typography sx={{ fontSize: 11, color: cm.textMuted }}>
              Axis widened to show out-of-hours bookings.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/** The portrait early/late band row — the whole row is the widen control. */
function PortraitBandRow({ label, onWiden, cm }: { label: string; onWiden: () => void; cm: CalendarMaps }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onWiden}
      aria-label={`${label} — widen the axis`}
      sx={{
        font: 'inherit',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        width: '100%',
        minHeight: PORTRAIT_BAND_HEIGHT,
        px: 1.75,
        border: 0,
        borderBottom: `1px solid ${cm.gridLine}`,
        bgcolor: cm.cancelledFill,
        textAlign: 'left',
      }}
    >
      <Typography variant="mono" sx={{ fontSize: 12, color: cm.textMuted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: cm.todayAccent, ml: 'auto' }}>
        Expand ›
      </Typography>
    </Box>
  );
}
